/* eslint-disable react/no-unstable-nested-components */
import { CellContext, ColumnDef, SortingState } from '@tanstack/react-table';
import Btn, { BtnModifier } from 'components/btn';
import ArrowRightIcon from 'assets/icons/arrowRight.svg';
import { StatusEnum, TableWrapperProps } from 'models/general';
import { useLocation, useNavigate } from 'react-router-dom';
import { useContext, useMemo, useState } from 'react';
import IconList, { IconListModifier } from 'components/iconList';
import { useTranslation } from 'react-i18next';
import { StoreContext } from 'models/import';
import PlatformCard, { PlatformCardModifier } from 'components/platformCard';
import Tooltip, { TooltipAlignEnum, TooltipCursorEnum } from 'components/tooltip';
import StatusIcon, { StatusIconModifier } from 'components/statusIcon';
import ImportTableFilters from './filters/importTableFilters';
import { BalanceStatusEnum } from '../../pages/overview/balanceStatusTooltip';
import { getFormattedNumber, LocationHistoryMismatch } from './balanceTable';
import { TableBaseApiKind } from './base/tableBaseApi';
import LegacyTableBase, { getDefaultEmptyValue } from './base/legacyTableBase';
import { TableModifier } from './base/tableBase';

export interface ImportTableApiJob {
  error: boolean | null;
}

export interface ImportTableBalanceStatus {
  balanceStatus: number;
  count: number;
}

export const getImportTableBalanceStatus = (value: BalanceStatusEnum): ImportTableBalanceStatus[] => {
  return [{
    balanceStatus: value,
    count: 1,
  }];
};

export interface ImportTableStatus {
  type: StatusEnum;
  message: string | null;
}

export interface ImportTableDef {
  transaction: any;
  name: string;
  type: string;
  logo_uri: string;
  display_name: string | null;
  custom_name: string | null;
  custom_names: string[] | null;
  transactions_count: number;
  last_date_modified: string;
  custom_api_uri: string | null;
  active_api_jobs: ImportTableApiJob[] | null;
  balance_status_summary: ImportTableBalanceStatus[];
  fiat_total_value: number;
}

function ImportTable(props: TableWrapperProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { pageSize = 50 } = props;
  const { t } = useTranslation();
  const { userSettings, importTableSettings, setImportTableSettings } = useContext(StoreContext);
  const [filters, setFilters] = useState('');
  const [currentMismatch, setCurrentMismatch] = useState<boolean | undefined>(undefined);
  const [userCurrencyMismatch, setUserCurrencyMismatch] = useState<boolean | undefined>(undefined);

  const columnVisibility = {
    logo_uri: false,
    type: false,
    custom_api_uri: false,
  };

  const initialSorting: SortingState = [{
    id: 'custom_names',
    desc: false,
  }];

  const onDataLoaded = () => {
    // reset table settings apart from mismatches once data was loaded
    setImportTableSettings(undefined);
  };

  const onNavigate = (url: string | null) => {
    if (url && url.length) {
      window.location.href = url;
    }
  };

  const onDetailNavigate = (
    info: CellContext<ImportTableDef, unknown>,
  ) => {
    const tableState = info.table.getState();
    const { sorting } = tableState;
    const { pageIndex, pageSize: pSize } = tableState.pagination;

    const name = info.getValue();
    const { type, wallet, live_only: liveOnly } = info.row.original;

    setImportTableSettings({ pageIndex, pageSize: pSize, sorting });

    // Needed to have a consistent browser navigation between /overview and /import-new
    const { origin } = window.location;
    window.history.replaceState(null, '', `${origin}/overview-v1`);

    const liveOnlyRoute = liveOnly ? 'live/' : '';

    if ((type === 'blockchain' || liveOnly) && wallet && name) {
      const walletAddress = wallet.toString().toLowerCase();
      navigate(`/overview-v1/${liveOnlyRoute}${type}/${name as string}/${walletAddress}`, {
        state: { data: currentMismatch, dataCurrency: userCurrencyMismatch },
      });
    } else {
      navigate(name ? `/overview-v1/${liveOnlyRoute}${type}/${name as string}` : `/overview-v1/${liveOnlyRoute}${type}`, {
        state: { data: currentMismatch, dataCurrency: userCurrencyMismatch },
      });
    }
  };

  const importTableColumns: ColumnDef<ImportTableDef>[] = useMemo(() => [
    {
      accessorKey: 'display_name',
      cell: (info) => {
        const displayName: string = info.getValue() as string | '';
        const logoUri: string = info.row.original.logo_uri;

        if (!displayName) {
          return (
            <PlatformCard
            logo_uri={undefined}
            name={'---'}
            display_name={'---'}
            beta={false}
            modifiers={[PlatformCardModifier.OLD]}
          />
          );
        }

        return (
          <PlatformCard
            logo_uri={logoUri}
            name={displayName}
            display_name={displayName}
            beta={false}
            modifiers={[PlatformCardModifier.OLD]}
          />
        );
      },
      header: t('Imported from', { ns: 'overview' }),
    },
    {
      accessorKey: 'logo_uri',
      cell: (info) => <b>{info.getValue() as string | ''}</b>,
    },
    {
      accessorKey: 'custom_api_uri',
      cell: (info) => <b>{info.getValue() as string | ''}</b>,
    },
    {
      accessorKey: 'custom_names',
      cell: (info) => {
        const items: string[] = info.getValue() as string[] | [];
        const translatedItems: string[] = items
          .map((i) => (i === '* no value *' ? t('* no value *', { ns: 'partner' }) : i));

        return !!items && items.length
          ? (
            <IconList
              items={translatedItems}
              modifiers={[
                IconListModifier.TABLE_SPACING, IconListModifier.OLD,
              ]}
            />
          )
          : getDefaultEmptyValue();
      },
      header: t('Custom Name', { ns: 'overview' }),
    },
    // TODO: implement in later stage
    // {
    //   accessorKey: 'types',
    //   cell: (info) => {
    //     const items: string[] = info.getValue();

    //     return (
    //       <>
    //         {items.map((i, index) => (
    //           <Pill key={index} modifiers={[PillModifier.TABLE]} value={i} />
    //         ))}
    //       </>
    //     );
    //   },
    //   header: 'Type',
    // },
    {
      accessorKey: 'transactions_count',
      header: t('Transactions', { ns: 'overview' }),
      cell: (info) => <span key={info.getValue() as string | ''}>{info.getValue() as string | ''}</span>,
    },
    {
      accessorKey: 'last_date_modified',
      header: t('Last Updated', { ns: 'overview' }),
    },
    {
      accessorKey: 'active_api_jobs',
      header: t('Active Imports', { ns: 'overview' }),
      cell: (info) => {
        const value: ImportTableApiJob[] = info.getValue() as ImportTableApiJob[] | [];
        if (!value) return getDefaultEmptyValue();

        const itemCount = value.length;
        if (itemCount === 0) return getDefaultEmptyValue();

        const apiUri: string = info.row.original.custom_api_uri || '';
        const apiType: string = info.row.original.type || '';
        let typeString = 'Import';
        if (apiType === 'blockchain') {
          typeString = t(itemCount < 2 ? 'Wallet' : 'Wallets', { ns: 'overview' });
        } else if (apiType === 'exchange') {
          typeString = t(itemCount < 2 ? 'API' : 'APIs', { ns: 'overview' });
        }

        const errorCount = value.filter((item) => (
          item != null && item.error != null && item.error !== false
        )).length;

        const errorString = t(errorCount < 2 ? 'Error' : 'Errors', { ns: 'overview' });

        const itemValue = (typeString === 'Import' && itemCount === 1)
          ? 'Some Import'
          : `${itemCount < 2 ? '' : `${itemCount} `}${typeString}`;
        const errorValue = `${errorCount < 2 ? '' : `${errorCount} `}${errorString}`;

        return (
          <>
            {itemCount > 0 && apiUri === '' && (
              <p>
                {itemValue}
              </p>
            )}
            {apiUri !== '' && (
              <Btn
                type="link"
                modifier={[BtnModifier.TABLE_LINK]}
                text={itemValue}
                onClick={() => onNavigate(apiUri)}
              />
            )}
            {apiUri !== '' && errorCount > 0 && (
              <br />
            )}
            {errorCount > 0 && (
              <Btn
                type="link"
                modifier={[BtnModifier.TABLE_LINK]}
                text={errorValue}
                onClick={() => onNavigate(apiUri)}
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: 'fiat_total_value',
      header: t('Total Value', { ns: 'overview' }),
      cell: (info) => {
        const value: number = info.getValue();
        const formattedValue: JSX.Element | null = getFormattedNumber(
          value,
          userSettings?.decimalCharacter,
          userSettings?.thousandCharacter,
          userSettings.currency,
        );
        return (
          <span key={value}>
            {formattedValue}
            {' '}
            {userSettings.currency}
          </span>
        );
      },
    },
    {
      accessorKey: 'balance_status_summary',
      header: t('Status', { ns: 'overview' }),
      enableSorting: false,
      cell: (info) => {
        const items: ImportTableBalanceStatus[] = info.getValue() as ImportTableBalanceStatus[]
        | [];
        if (!items || items.length === 0) return getDefaultEmptyValue();
        const textValue: (string | null)[] = items.map((item: ImportTableBalanceStatus) => {
          if (item.count === 0) {
            return null;
          }
          let text: null | string = null;
          switch (item.balanceStatus) {
            case BalanceStatusEnum.MissingLiveData:
              if (item.count > 1) {
                text = `${item.count} ${t('Balance mismatches', { ns: 'overview' })}`;
              } else {
                text = `${item.count} ${t('Balance mismatch', { ns: 'overview' })}`;
              }
              break;
            case BalanceStatusEnum.MissingImportedData:
              if (item.count > 1) {
                text = `${item.count} ${t('Balance mismatches', { ns: 'overview' })}`;
              } else {
                text = `${item.count} ${t('Balance mismatch', { ns: 'overview' })}`;
              }
              break;
            case BalanceStatusEnum.MismatchingLiveData:
              if (item.count > 1) {
                text = `${item.count} ${t('Balance mismatches', { ns: 'overview' })}`;
              } else {
                text = `${item.count} ${t('Balance mismatch', { ns: 'overview' })}`;
              }
              break;
            case BalanceStatusEnum.UnexpectedNegativeValues:
              if (item.count > 1) {
                text = `${item.count} ${t('Unexpected negative balances', { ns: 'overview' })}`;
              } else {
                text = `${item.count} ${t('Unexpected negative balance', { ns: 'overview' })}`;
              }
              break;
            case BalanceStatusEnum.EmptyCoinTicker:
              text = `${item.count} ${t('Empty coin-ticker', { ns: 'overview' })}${item.count > 1 ? 's' : ''}`;
              break;
            default:
              return null;
          }
          return text || null;
        });
        const title: string[] = textValue.filter((v) => v !== null);

        let statusValue: StatusEnum;
        if (items.some((e: ImportTableBalanceStatus) => e.count > 0
        && e.balanceStatus === BalanceStatusEnum.MismatchingLiveData)) {
          statusValue = StatusEnum.WARNING;
        } else if (items.some((e: ImportTableBalanceStatus) => e.count > 0
        && e.balanceStatus === BalanceStatusEnum.MissingLiveData)) {
          statusValue = StatusEnum.WARNING;
        } else if (items.some((e: ImportTableBalanceStatus) => e.count > 0
        && e.balanceStatus === BalanceStatusEnum.MissingImportedData)) {
          statusValue = StatusEnum.WARNING;
        } else if (items.some((e: ImportTableBalanceStatus) => e.count > 0
        && e.balanceStatus === BalanceStatusEnum.UnexpectedNegativeValues)) {
          statusValue = StatusEnum.WARNING;
        } else if (items.some((e: ImportTableBalanceStatus) => e.count > 0
        && e.balanceStatus === BalanceStatusEnum.EmptyCoinTicker)) {
          statusValue = StatusEnum.WARNING;
        } else {
          statusValue = StatusEnum.SUCCESS;
        }

        return title.length > 0 ? (
          <Tooltip
            align={TooltipAlignEnum.RIGHT}
            cursor={TooltipCursorEnum.HELP}
            tooltipChildren={(
              <IconList
                items={title}
                modifiers={[
                  IconListModifier.TABLE_SPACING, IconListModifier.OLD,
                ]}
              />
            )}
          >
            <StatusIcon type={statusValue} modifiers={[StatusIconModifier.OVERVIEW]} />
          </Tooltip>
        ) : <StatusIcon type={statusValue} modifiers={[StatusIconModifier.OVERVIEW]} />;
      },
    },
    {
      accessorKey: 'name',
      header: '',
      enableSorting: false,
      cell: (info) => (
        <Btn
          type="link"
          modifier={[BtnModifier.TABLE_LINK]}
          text={t('See Details', { ns: 'overview' })}
          icon={<ArrowRightIcon />}
          iconRight
          onClick={() => {
            onDetailNavigate(info);
          }}
        />
      ),
    },
  ], [currentMismatch, userCurrencyMismatch]);

  return (
    <>
      <ImportTableFilters
        useLocalSearch={false}
        ignoreSmallMismatchesProp={(location.state as LocationHistoryMismatch !== undefined)
          && (location.state as LocationHistoryMismatch !== null)
          && ((location.state as LocationHistoryMismatch).data !== undefined)
          && ((location.state as LocationHistoryMismatch).data !== null)
          ? (location.state as LocationHistoryMismatch).data! : true}
        ignoreUserCurrencyMismatchesProp={(location.state as LocationHistoryMismatch !== undefined)
          && (location.state as LocationHistoryMismatch !== null)
          && ((location.state as LocationHistoryMismatch).dataCurrency !== undefined)
          && ((location.state as LocationHistoryMismatch).dataCurrency !== null)
          ? (location.state as LocationHistoryMismatch).dataCurrency! : true}
        onSmallMismatchUpdate={(value: boolean) => setCurrentMismatch(value)}
        onUserCurrencyMismatchUpdate={(value: boolean) => setUserCurrencyMismatch(value)}
        onFitlerCB={(value: string) => setFilters(value)} 
        onGlobalFilterUpdate={() =>{}} //only for v2
        disabled={false}      />
      <LegacyTableBase
        requestUri={props.requestUri ? props.requestUri : '/tx-overview-v1/overview'}
        filters={filters}
        requiresFiltersForFirstLoad
        apiKind={TableBaseApiKind.LAMINAS}
        columns={importTableColumns}
        columnVisibility={columnVisibility}
        initialSorting={importTableSettings ? importTableSettings.sorting : initialSorting}
        modifier={TableModifier.IMPORT_TABLE}
        pageSize={importTableSettings ? importTableSettings.pageSize : pageSize}
        pageIndex={importTableSettings ? importTableSettings.pageIndex : 0}
        onDataLoaded={() => onDataLoaded()}
      />
    </>
  );
}

export default ImportTable;
